import React, { useState } from "react";
import { Link } from "react-router-dom";
import { priceCart, qrItemsOf, runCartPayment } from "../../config/cart";
import { subscriptionService, ORDER_TYPE } from "../../services";
import { boostService, addonService, customRequestService } from "../../services/addons";
import { useApp } from "../../context/AppContext";
import { useCart } from "../../context/CartContext";
import { useI18n } from "../../i18n";
import { Badge, Button, Card, EmptyState, PageHead, Qty, baht, formatDate, money, useAsync } from "../../components/ui";
import Icon from "../../components/ui/Icon";
import PaymentQrModal from "../../components/PaymentQrModal";

const KIND_LABEL = {
  plan: { th: "แพ็กเกจหลัก", en: "Main plan", tone: "indigo" },
  points: { th: "Point", en: "Points", tone: "amber" },
  addon: { th: "ฟีเจอร์เสริม", en: "Add-on", tone: "sky" },
  custom: { th: "ฟีเจอร์ custom", en: "Custom feature", tone: "green" },
};

export default function Cart() {
  const { organization, orgId, profile, refreshOrganizations, toast } = useApp();
  const { items, remove, setQty, clear } = useCart();
  const { tx } = useI18n();
  const [checkout, setCheckout] = useState(null);

  const by = profile?.fullname || profile?.email || "";
  // ฟีเจอร์ custom: ราคาอ่านจากใบเสนอราคาล่าสุดในชีตทุกครั้งที่แสดง/จ่าย ไม่เก็บราคาในตะกร้า
  const hasCustom = items.some((row) => row.kind === "custom");
  const { data: customRequests, loading: customLoading, reload: reloadCustom } = useAsync(
    () => (hasCustom ? customRequestService.listCustom(orgId) : Promise.resolve([])),
    [orgId, hasCustom],
    []
  );
  const priced = priceCart(items, organization, { customRequests: customRequests || [] });

  const noteText = (line) => {
    switch (line.note) {
      case "needPlan":
        return tx("ต้องมีแพ็กเกจหลักที่ยังไม่หมดอายุ — ใส่แพ็กเกจลงตะกร้าหรือลบรายการนี้ออกก่อนชำระเงิน", "Needs an active main plan — add a plan to the cart or remove this item before paying");
      case "inPlan":
        return tx("แพ็กเกจนี้มีโมดูลนี้อยู่แล้ว ไม่จำเป็นต้องซื้อฟีเจอร์เสริม — แนะนำให้ลบออก", "The plan already includes this module, so the extra feature is not needed — consider removing it");
      case "unlimited":
        return tx("แพ็กเกจนี้ไม่จำกัดโควตานี้อยู่แล้ว ไม่จำเป็นต้องซื้อ — แนะนำให้ลบออก", "The plan already has unlimited quota here, so this is not needed — consider removing it");
      case "covered":
        return tx("รายการนี้ใช้ได้ถึงวันหมดอายุแพ็กเกจอยู่แล้ว ไม่จำเป็นต้องต่ออายุ — แนะนำให้ลบออก", "This item already lasts until the plan ends, so renewing is not needed — consider removing it");
      case "customMissing":
        return customLoading
          ? tx("กำลังตรวจใบเสนอราคา...", "Checking the quotation...")
          : tx("ไม่พบคำขอนี้ กรุณาลบออก", "This request was not found — please remove it");
      case "customNotPayable":
        return tx("คำขอนี้ไม่อยู่ในสถานะรอชำระเงิน (ชำระแล้ว/ยกเลิก/รอเสนอราคา) กรุณาลบออก", "This request is not awaiting payment (paid, cancelled or not quoted yet) — please remove it");
      case "customInvalidQuote":
        return tx("ใบเสนอราคาไม่ถูกต้อง กรุณาติดต่อทีมงาน", "This quotation is invalid. Please contact our team.");
      case "renewMissing":
        return tx("ไม่พบรายการเดิมที่จะต่ออายุ กรุณาลบออก", "The item to renew was not found — please remove it");
      default:
        return tx("รายการนี้ใช้ไม่ได้แล้ว กรุณาลบออก", "This item is no longer valid — please remove it");
    }
  };

  const startPay = () => {
    // ล็อกรายการ+ราคา ณ ตอนกดจ่าย — แก้ตะกร้าระหว่างรอ QR ไม่กระทบยอดที่จ่ายไปแล้ว
    const lines = priced.lines;
    setCheckout({ lines, total: priced.total });
  };

  const onPaid = async (transaction) => {
    await runCartPayment({
      lines: checkout.lines,
      organization,
      transaction,
      by,
      deps: {
        activatePlan: subscriptionService.activate,
        activateAddon: addonService.activate,
        topup: boostService.topup,
        payCustom: customRequestService.markPaid,
        refreshOrganizations,
      },
    });
    await refreshOrganizations();
    reloadCustom();
    clear();
    toast(tx("ชำระเงินและเปิดใช้รายการในตะกร้าเรียบร้อย", "Payment complete — everything in your cart is activated"), "success");
  };

  return (
    <>
      <PageHead
        title={tx("ตะกร้า", "Cart")}
        subtitle={tx("รวมแพ็กเกจ ฟีเจอร์เสริม ฟีเจอร์ custom และ point ชำระ QR ครั้งเดียว · ราคายังไม่รวม VAT 7%", "Combine plan, add-ons and points into one QR payment · excludes 7% VAT")}
        actions={
          items.length ? (
            <Button size="sm" onClick={clear}>
              {tx("ล้างตะกร้า", "Clear cart")}
            </Button>
          ) : null
        }
      />

      {!items.length ? (
        <Card>
          <EmptyState
            icon={<Icon name="cart" />}
            text={tx("ตะกร้ายังว่างอยู่", "Your cart is empty")}
            action={
              <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                <Link to="/admin/subscription"><Button variant="primary">{tx("เลือกแพ็กเกจ", "Choose a plan")}</Button></Link>
                <Link to="/admin/addons"><Button>{tx("ฟีเจอร์เสริม & Point", "Add-ons & points")}</Button></Link>
              </div>
            }
          />
        </Card>
      ) : (
        <div className="grid-2" style={{ alignItems: "start" }}>
          <div style={{ display: "grid", gap: 10 }}>
            {priced.lines.map((line) => {
              const label = KIND_LABEL[line.kind] || KIND_LABEL.addon;
              const warn = line.status !== "ok";
              const blocked = line.status === "blocked" || line.status === "invalid";
              const canQty = line.kind === "points" || (line.kind === "addon" && line.addonKind === "quota" && !line.renewId);
              const qtyValue = line.kind === "points" ? line.qty : line.blocks;
              return (
                <div
                  key={line.id}
                  className="card-crm"
                  style={{ marginBottom: 0, borderColor: blocked ? "var(--c-rose)" : warn ? "#fdba74" : undefined, background: blocked ? "#fff1f2" : warn ? "#fff7ed" : undefined }}
                >
                  <div className="u-between" style={{ alignItems: "flex-start", gap: 12 }}>
                    <div style={{ minWidth: 0 }}>
                      <Badge tone={label.tone}>{tx(label.th, label.en)}</Badge>
                      <div className="u-strong u-mt-8">{tx(line.name, line.nameEn)}</div>
                      {line.kind === "plan" ? (
                        <div className="u-fs-12 u-muted">
                          {tx(`ใช้ได้ถึง ${formatDate(line.expireAt)}`, `Valid until ${formatDate(line.expireAt)}`)}
                        </div>
                      ) : null}
                      {line.kind === "points" ? (
                        <div className="u-fs-12 u-muted">
                          {tx(`ได้ ${money(line.totalPoints, 0)} point (${baht(line.unit, 0)} × ${line.qty})`, `${money(line.totalPoints, 0)} points (${baht(line.unit, 0)} × ${line.qty})`)}
                        </div>
                      ) : null}
                      {line.kind === "custom" && line.status === "ok" ? (
                        <div className="u-fs-12 u-muted">
                          {tx(`${money(line.mandays, 2)} manday × ${baht(line.rate, 0)} (ตามใบเสนอราคา)`, `${money(line.mandays, 2)} mandays × ${baht(line.rate, 0)} (per quotation)`)}
                        </div>
                      ) : null}
                      {line.kind === "addon" && line.status !== "invalid" && line.status !== "blocked" ? (
                        <div className="u-fs-12 u-muted">
                          {tx(`คิดตามวันที่เหลือ ${line.days} วัน · หมดพร้อมแพ็กเกจหลัก ${formatDate(line.expireAt)}`, `Prorated for ${line.days} days · ends with the plan on ${formatDate(line.expireAt)}`)}
                        </div>
                      ) : null}
                    </div>
                    <div style={{ textAlign: "right", flex: "0 0 auto" }}>
                      {line.status === "invalid" || line.status === "blocked" ? (
                        <div className="u-muted">-</div>
                      ) : (
                        <div className="u-strong" style={{ fontSize: 18 }}>{baht(line.amount, 0)}</div>
                      )}
                    </div>
                  </div>

                  {warn ? (
                    <div className={`u-fs-13 u-mt-8 ${blocked ? "u-danger" : ""}`} style={blocked ? undefined : { color: "#c2410c" }}>
                      <Icon name="warning" /> {noteText(line)}
                    </div>
                  ) : null}

                  <div className="u-between u-mt-8">
                    {canQty ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span className="u-fs-13">{line.kind === "points" ? tx("จำนวน", "Quantity") : tx("จำนวนก้อน", "Blocks")}</span>
                        <Qty value={qtyValue} min={1} max={line.kind === "points" ? 99 : 20} onChange={(value) => setQty(line.id, value)} />
                      </div>
                    ) : (
                      <span />
                    )}
                    <Button size="sm" onClick={() => remove(line.id)}>
                      <Icon name="trash" /> {tx("ลบ", "Remove")}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          <Card title={tx("สรุปยอด", "Summary")}>
            <div style={{ display: "grid", gap: 8 }}>
              {priced.lines
                .filter((line) => line.status === "ok" || line.status === "unneeded")
                .map((line) => (
                  <div key={line.id} className="u-between u-fs-13" style={{ gap: 12 }}>
                    <span style={{ minWidth: 0 }}>{tx(line.name, line.nameEn)}{line.qty > 1 ? ` × ${line.qty}` : ""}</span>
                    <span>{baht(line.amount, 0)}</span>
                  </div>
                ))}
            </div>
            <div className="u-between u-mt-16 u-mb-16" style={{ borderTop: "1px solid #e2e8f0", paddingTop: 12 }}>
              <span className="u-strong">{tx("ยอดรวม", "Total")}</span>
              <span className="u-strong" style={{ fontSize: 24 }}>{baht(priced.total, 0)}</span>
            </div>
            {priced.hasPlan ? (
              <div className="u-fs-12 u-muted u-mb-8">
                {tx(`ฟีเจอร์เสริมคิดราคาตามวันหมดอายุหลังชำระตะกร้านี้ (${formatDate(priced.expireAfter)})`, `Add-ons are prorated to the plan end after this payment (${formatDate(priced.expireAfter)})`)}
              </div>
            ) : null}
            {!priced.canPay ? (
              <div className="u-fs-12 u-danger u-mb-8">
                {tx("มีรายการที่ชำระไม่ได้ กรุณาแก้ไขหรือลบออกก่อน", "Some items cannot be paid — fix or remove them first")}
              </div>
            ) : null}
            <Button variant="primary" block disabled={!priced.canPay || priced.total <= 0} onClick={startPay}>
              {tx(`ชำระเงิน ${baht(priced.total, 0)}`, `Pay ${baht(priced.total, 0)}`)}
            </Button>
            <div className="u-fs-12 u-muted u-mt-8">
              {tx("ชำระด้วย QR PromptPay ครั้งเดียว ระบบเปิดใช้งานทุกรายการให้อัตโนมัติ", "One QR PromptPay payment — every item is activated automatically")}
            </div>
          </Card>
        </div>
      )}

      <PaymentQrModal
        open={!!checkout}
        title={tx("ชำระตะกร้า", "Cart payment")}
        subtitle={organization?.name}
        amount={checkout?.total || 0}
        orderType={ORDER_TYPE.cart}
        reference={`cart:${orgId}`}
        items={checkout ? qrItemsOf(checkout.lines.filter((line) => line.status === "ok" || line.status === "unneeded"), orgId) : []}
        onPaid={onPaid}
        onClose={() => setCheckout(null)}
      />
    </>
  );
}
